import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Radio, Tooltip } from "@mui/material";
import Plus from "assets/img/svg/plus";
import ContractEditorLexical from "components/ContractsSettings/ContractEditorLexical";
import { articlesInWorkOrder } from "components/ContractsSettings/ContractEditorLexical/RepeatablePlugin/repeatBy";
import LoadingWrapper from "components/shared/LoadingWrapper";
import ScrollShadowWrapper from "components/shared/ScrollShadowWrapper";
import StyledButton from "components/shared/StyledButton";
import { PortalType } from "contexts/portal.context";
import { H3, SmallBody, SmallBodyHighlight, TinyBody } from "helpers/TypographyComponents";
import { isObject } from "helpers/utils";
import { useWorkTitles } from "hooks/useWorkTitles";
import { useContext, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import type { AgreementTemplate } from "store/agreementsStore";
import { useAppSelector } from "store/store";
import { EPortalType } from "store/types";

import type { TFormCreateAgreement, TTitleBeneficiaries } from "../utils";

interface IChooseTemplateStep {
    onClose: () => void;
    handleNextStep: () => void;
    currentTemplateId: number;
    setCurrentTemplateId: React.Dispatch<React.SetStateAction<number>>;
    setEditorState: React.Dispatch<React.SetStateAction<{ stringifiedBlocks: string; html: string }>>;
    hasMultiSignature: boolean;
    handlePreviousStep: () => void;
    titleBeneficiaries: TTitleBeneficiaries | undefined;
    beneficiaryId: number;
    workId?: number | null;
    isDocoon?: boolean;
    /**
     * When true, the primary button submits the form. When false, it advances to the next
     * wizard step. Callers that add a step after this one (e.g. the Docoon send-options step
     * when the validation chain is enabled) must set this to false.
     */
    isLastStep?: boolean;
    /**
     * Filter templates by document type
     */
    documentType?: "agreement" | "avenant" | "framework_contract";
    // Force the previous button even without multi-signature (when an earlier step is visible).
    hasPreviousStep?: boolean;
}

const ChooseTemplateStep = ({
    onClose,
    handleNextStep,
    currentTemplateId,
    setCurrentTemplateId,
    setEditorState,
    hasMultiSignature,
    handlePreviousStep,
    titleBeneficiaries,
    beneficiaryId,
    workId,
    isDocoon,
    isLastStep,
    documentType,
    hasPreviousStep,
}: IChooseTemplateStep) => {
    const [t] = useTranslation();
    const navigate = useNavigate();
    const portalType = useContext(PortalType);
    const repeatsByArticle = portalType === EPortalType.BOOK && workId != null;
    const { titles: workTitles, isLoading: workTitlesLoading } = useWorkTitles(repeatsByArticle ? workId : null);
    const articlesReady = !repeatsByArticle || !workTitlesLoading;
    const articles = useMemo(
        () => articlesInWorkOrder(workTitles).map((title) => ({ id: title.id, name: title.name })),
        [workTitles],
    );
    const { watch } = useFormContext<TFormCreateAgreement>();
    const { sendAgreementStatus } = useAppSelector((state) => state.agreements.agFetchStatus);

    /**
     * Fallback current beneficiary id of term, if there is no other signatories, or no mutisignature
     */
    const beneficiaryIds = watch("beneficiaryIds") ?? [beneficiaryId];

    const { agreementsTemplate, defaultAgreementTemplate } = useAppSelector((state) => state.agreements);

    const getTemplateName = (template: AgreementTemplate) =>
        template?.id === defaultAgreementTemplate?.id ? t("ContractSettings.DefaultTemplate") : template?.name;

    const filteredTemplates = useMemo(() => {
        const all = [defaultAgreementTemplate, ...agreementsTemplate] as AgreementTemplate[];
        const effectiveType = documentType ?? "agreement";
        return all.filter((t) => t?.type === effectiveType);
    }, [defaultAgreementTemplate, agreementsTemplate, documentType]);

    const currentTemplate = useMemo(() => {
        return filteredTemplates.find((template) => template?.id == currentTemplateId) ?? filteredTemplates[0];
    }, [currentTemplateId, filteredTemplates]);

    const handleModelChose = (id: number) => {
        setCurrentTemplateId(id);
    };

    const renderValidatorsLabel = (template: AgreementTemplate) => {
        if (!template?.validators) return;
        const validatorNames = template.validators
            .map((user) => `${user?.firstname ?? ""} ${user?.lastname ?? ""}`)
            .join(", ");
        const validatorsCount = template.validators.length;
        return `${
            validatorsCount > 1 ? t("ContractSettings.ValidatorLabelPlural") : t("ContractSettings.ValidatorLabel")
        } ${validatorNames}`;
    };

    const isTemplateReady =
        currentTemplate && isObject(currentTemplate?.blocks) && !("blocks" in currentTemplate.blocks);

    return (
        <div className="agreement-step-1 and-validation">
            <div className="agreement-step-1-choose-model">
                <ScrollShadowWrapper className="agreement-step-1-choose-model-list" style={{ paddingRight: "0" }}>
                    <H3 color="grey" className="agreement-step-1-choose-model-head">
                        {t("ContractSettings.Model")}
                    </H3>
                    {filteredTemplates?.map((template) => {
                        const validatorsCount = template?.validators?.length;

                        if (template) {
                            return (
                                <Tooltip
                                    title={validatorsCount > 0 && renderValidatorsLabel(template)}
                                    key={template.id}
                                >
                                    <div
                                        className={`agreement-step-1-choose-model-element ${
                                            template.id == currentTemplateId ? "active" : ""
                                        }`}
                                        onClick={() => handleModelChose(template.id)}
                                    >
                                        <Radio
                                            checked={template.id == currentTemplateId}
                                            checkedIcon={<CheckCircleIcon />}
                                            size="small"
                                        />
                                        <div>
                                            {template.id == currentTemplateId ? (
                                                <SmallBodyHighlight className="agreement-step-1-choose-model-element-text">
                                                    {getTemplateName(template)}
                                                </SmallBodyHighlight>
                                            ) : (
                                                <SmallBody
                                                    color="grey"
                                                    className="agreement-step-1-choose-model-element-text"
                                                >
                                                    {getTemplateName(template)}
                                                </SmallBody>
                                            )}
                                            {validatorsCount > 0 && (
                                                <TinyBody
                                                    color="grey"
                                                    style={{ paddingLeft: "8px" }}
                                                >{`${validatorsCount} ${t("ContractSettings.ValidatorsLabel")}${
                                                    validatorsCount > 1 ? "s" : ""
                                                }`}</TinyBody>
                                            )}
                                        </div>
                                    </div>
                                </Tooltip>
                            );
                        }
                    })}
                    <div className="agreement-step-1-choose-model-new" onClick={() => navigate("/settings/contracts")}>
                        <Plus fill="var(--foreground)" />
                        {t("ContractSettings.NewModel")}
                    </div>
                </ScrollShadowWrapper>
                <div className="agreement-step-1-choose-model-template-wrapper">
                    <div className="agreement-step-1-choose-model-template">
                        <div className="agreement-step-1-choose-model-template-content">
                            {filteredTemplates.length === 0 ? (
                                <div className="flex items-center justify-center h-full">
                                    <SmallBody color="grey">{t("ContractSettings.NoTemplate")}</SmallBody>
                                </div>
                            ) : (
                                <LoadingWrapper loading={!isTemplateReady || !articlesReady} type="loading-page">
                                    <ContractEditorLexical
                                        agreement={currentTemplate!}
                                        isAgreementSetup
                                        isReadOnly
                                        onSetupSave={(data: any) => setEditorState(data)}
                                        key={currentTemplate?.id}
                                        beneficiariesFormData={{ beneficiaryIds, titleBeneficiaries }}
                                        articlesFormData={articles.length ? { articles } : undefined}
                                    />
                                </LoadingWrapper>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <div className="agreement-step-1-actions">
                <div style={{ display: "flex", gap: 8 }}>
                    <StyledButton buttonType="secondary" onClick={onClose}>
                        {t("Cancel")}
                    </StyledButton>
                    {(hasMultiSignature || hasPreviousStep) && (
                        <StyledButton buttonType="secondary" onClick={handlePreviousStep}>
                            {hasMultiSignature
                                ? t("ContractValidation.ReturnToBeneficiaries")
                                : t("NewElement.Previous")}
                        </StyledButton>
                    )}
                </div>
                {isDocoon && isLastStep ? (
                    <StyledButton buttonType="primary" type="submit" loading={sendAgreementStatus === "pending"}>
                        {t("ContractValidation.GenerateContract")}
                    </StyledButton>
                ) : (
                    <StyledButton buttonType="primary" onClick={handleNextStep}>
                        {t("NewElement.Continue")}
                    </StyledButton>
                )}
            </div>
        </div>
    );
};

export default ChooseTemplateStep;
